// ✅ RECOMMENDED FIX: normalize priority value (always store as UPPERCASE key)
// Drop-in changes only (safe + minimal)

import {
  Popover,
  Box,
  Typography,
  Divider,
  FormControl,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Chip,
  Button,
  Switch,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  getStatusUI,
  capitalize,
  getPriorityStyles,
  PRIORITY_OPTIONS,
} from "../../../config/leadStatus";

// --- helpers ---
const normalizePriorityKey = (v) => {
  if (!v) return "all";
  if (v === "all") return "all";
  return String(v).trim().toUpperCase(); // "High" -> "HIGH"
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  color: "#64748B",
  letterSpacing: 0.5,
  textTransform: "uppercase",
  mb: 1,
};

const selectSx = {
  borderRadius: 2,
  bgcolor: "#F8FAFC",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
  "& .MuiSelect-select": { py: 1.25, fontSize: 14, color: "#334155" },
};

const radioSx = {
  color: "#CBD5E1",
  "&.Mui-checked": { color: "primary.main" },
  padding: "6px",
};

const radioLabelSx = {
  "& .MuiFormControlLabel-label": { fontSize: 14, color: "#334155" },
  marginRight: 3,
};

// Helper to lighten a hex color for chip background
const hexToLightBg = (hex) => {
  if (!hex || !hex.startsWith("#")) return "#F1F5F9";
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = 0.85;
  return `rgb(${Math.round(r + (255 - r) * mix)}, ${Math.round(
    g + (255 - g) * mix,
  )}, ${Math.round(b + (255 - b) * mix)})`;
};

const getResponseChipStyle = (label, isSelected) => {
  const ui = getStatusUI(
    (label || "").toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_"),
  );
  const color = ui.color || "#64748B";
  return {
    bgcolor: isSelected ? color : hexToLightBg(color),
    color: isSelected ? "#fff" : color,
  };
};

const FilterPopover = ({
  open,
  anchorEl,
  onClose,
  pending,
  setPending,
  clearPending,
  applyPending,
  treatments,
  telecallers,
  responseLabels = [],
  sources,
}) => {
  const theme = useTheme();
  // ✅ ensure we compare normalized value in UI (prevents mismatch bugs)
  const selectedPriorityKey = normalizePriorityKey(pending.selectedPriority);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            width: 380,
            borderRadius: 3,
            border: "1px solid #E2E8F0",
            boxShadow: "0 20px 50px rgba(2,6,23,0.15)",
            overflow: "hidden",
            mt: 1,
          },
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>
          Filter Leads
        </Typography>
      </Box>
      <Divider />

      <Box
        sx={{
          maxHeight: "60vh",
          overflowY: "auto",
          px: 2.5,
          py: 2.5,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#fff",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.main,
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.primary.main} #fff`,
        }}
      >
        <Typography sx={{ ...sectionLabelSx }}>OWNERSHIP</Typography>

        <FormControlLabel
          label="Show My Leads Only"
          sx={{
            "& .MuiFormControlLabel-label": { fontSize: 14, color: "#334155" },
          }}
          control={
            <Switch
              checked={!!pending.myLeadsOnly}
              onChange={(e) =>
                setPending((p) => ({
                  ...p,
                  myLeadsOnly: e.target.checked,
                  selectedTelecaller: e.target.checked
                    ? "all"
                    : p.selectedTelecaller,
                }))
              }
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "primary.main",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "primary.main",
                },
              }}
            />
          }
        />

        {/* DATE RANGE */}
        <Typography mt={2} sx={sectionLabelSx}>
          DATE RANGE
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={pending.dateRange}
            onChange={(e) =>
              setPending((p) => ({ ...p, dateRange: e.target.value }))
            }
            sx={selectSx}
          >
            <MenuItem value="7">Last 7 Days</MenuItem>
            <MenuItem value="30">Last 30 Days</MenuItem>
            <MenuItem value="90">Last 90 Days</MenuItem>
          </Select>
        </FormControl>

        {/* LEAD SOURCE */}
        <Typography sx={{ ...sectionLabelSx, mt: 2.5 }}>LEAD SOURCE</Typography>
        <RadioGroup
          value={pending.selectedSource}
          onChange={(e) =>
            setPending((p) => ({ ...p, selectedSource: e.target.value }))
          }
        >
          <Stack
            direction="row"
            spacing={0}
            flexWrap="wrap"
            useFlexGap
            sx={{ gap: 0 }}
          >
            <FormControlLabel
              value="all"
              control={<Radio size="small" sx={radioSx} />}
              label="All"
              sx={radioLabelSx}
            />

            {sources
              .filter((s) => s && s !== "all")
              .map((src) => (
                <FormControlLabel
                  key={src}
                  value={src}
                  control={<Radio size="small" sx={radioSx} />}
                  label={capitalize(src)}
                  sx={radioLabelSx}
                />
              ))}
          </Stack>
        </RadioGroup>

        {/* TREATMENT TYPE */}
        <Typography sx={{ ...sectionLabelSx, mt: 2.5 }}>
          TREATMENT TYPE
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={pending.selectedTreatment}
            onChange={(e) =>
              setPending((p) => ({ ...p, selectedTreatment: e.target.value }))
            }
            sx={selectSx}
          >
            {treatments.map((t) => (
              <MenuItem key={t} value={t}>
                {t === "all" ? "All Treatments" : t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* TELECALLER */}
        <Typography sx={{ ...sectionLabelSx, mt: 2.5 }}>TELECALLER</Typography>
        <FormControl fullWidth size="small">
          <Select
            value={pending.selectedTelecaller || "all"}
            onChange={(e) =>
              setPending((p) => ({ ...p, selectedTelecaller: e.target.value }))
            }
            sx={selectSx}
          >
            <MenuItem value="all">All Telecallers</MenuItem>
            {telecallers.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ✅ PRIORITY (FIXED) */}
        <Typography sx={{ ...sectionLabelSx, mt: 2.5 }}>PRIORITY</Typography>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ gap: 1 }}
        >
          <Chip
            label="ALL"
            clickable
            onClick={() =>
              setPending((p) => ({ ...p, selectedPriority: "all" }))
            }
            sx={{
              height: 28,
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 11,
              px: 0.5,
              bgcolor: selectedPriorityKey === "all" ? "#334155" : "#F1F5F9",
              color: selectedPriorityKey === "all" ? "#fff" : "#64748B",
              border: "none",
              "&:hover": {
                bgcolor: selectedPriorityKey === "all" ? "#1E293B" : "#E2E8F0",
              },
            }}
          />

          {PRIORITY_OPTIONS.map((priority) => {
            // normalize option keys too
            const key = normalizePriorityKey(priority);
            const isSelected = selectedPriorityKey === key;

            // getPriorityStyles may expect original value, but key is safest.
            // If your getPriorityStyles expects "High", pass priority instead of key.
            const style = getPriorityStyles(key);

            return (
              <Chip
                key={key}
                label={String(style.label || key).toUpperCase()}
                clickable
                onClick={
                  () => setPending((p) => ({ ...p, selectedPriority: key })) // ✅ store UPPERCASE
                }
                sx={{
                  height: 28,
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 11,
                  px: 0.5,
                  bgcolor: isSelected ? style.color : style.bgcolor,
                  color: isSelected ? "#fff" : style.color,
                  border: "none",
                  "&:hover": { opacity: 0.9 },
                }}
              />
            );
          })}
        </Stack>

        {/* RESPONSE LABEL */}
        <Typography sx={{ ...sectionLabelSx, mt: 2.5 }}>
          RESPONSE LABEL
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ gap: 1 }}
        >
          <Chip
            label="ALL"
            clickable
            onClick={() =>
              setPending((p) => ({ ...p, selectedResponse: "all" }))
            }
            sx={{
              height: 28,
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 11,
              px: 0.5,
              bgcolor:
                pending.selectedResponse === "all" ? "#334155" : "#F1F5F9",
              color: pending.selectedResponse === "all" ? "#fff" : "#64748B",
              border: "none",
              "&:hover": {
                bgcolor:
                  pending.selectedResponse === "all" ? "#1E293B" : "#E2E8F0",
              },
            }}
          />
          {responseLabels.length > 0
            ? responseLabels.map((label) => {
                const isSelected =
                  pending.selectedResponse === label.toLowerCase();
                const style = getResponseChipStyle(label, isSelected);
                return (
                  <Chip
                    key={label}
                    label={capitalize(label).toUpperCase()}
                    clickable
                    onClick={() =>
                      setPending((p) => ({
                        ...p,
                        selectedResponse: label.toLowerCase(),
                      }))
                    }
                    sx={{
                      height: 28,
                      borderRadius: 999,
                      fontWeight: 700,
                      fontSize: 11,
                      px: 0.5,
                      bgcolor: style.bgcolor,
                      color: style.color,
                      border: "none",
                      "&:hover": { opacity: 0.9 },
                    }}
                  />
                );
              })
            : ["hot", "warm", "cold"].map((label) => {
                const isSelected = pending.selectedResponse === label;
                const style = getResponseChipStyle(label, isSelected);
                return (
                  <Chip
                    key={label}
                    label={label.toUpperCase()}
                    clickable
                    onClick={() =>
                      setPending((p) => ({ ...p, selectedResponse: label }))
                    }
                    sx={{
                      height: 28,
                      borderRadius: 999,
                      fontWeight: 700,
                      fontSize: 11,
                      px: 0.5,
                      bgcolor: style.bgcolor,
                      color: style.color,
                      border: "none",
                      "&:hover": { opacity: 0.9 },
                    }}
                  />
                );
              })}
        </Stack>
      </Box>

      <Divider />

      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={clearPending}
          sx={{
            fontWeight: 600,
            fontSize: 14,
            color: "#64748B",
            textTransform: "none",
            "&:hover": { bgcolor: "transparent", color: "#475569" },
          }}
        >
          Clear All
        </Button>
        <Button
          onClick={applyPending}
          variant="contained"
          sx={{
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
            bgcolor: theme.palette.primary.main,
            textTransform: "none",
            px: 2.5,
            boxShadow: "none",
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
              boxShadow: "none",
            },
          }}
        >
          Apply Filters
        </Button>
      </Box>
    </Popover>
  );
};

export default FilterPopover;
